import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import Tabs from "@/components/ui/v2/tabs";
import { URL_PARAMS } from "@/constants/urlParams";
import { useModuleStatus } from "@/features/integrations/api/useModuleStatus";
import { ModuleName } from "@/features/integrations/utils/module-name";

export type ReportLevel = "applies" | "candidates";

/**
 * Tab bar linking the Applies report and its Candidates level (OR-2155). Rendered at the top of both
 * pages. The levels live at `<base>` (Applies) and `<base>/candidates` (Candidates) in both the
 * tenant (`/applies`) and client (`/clients/:id/applies`) contexts. Hrefs preserve the current
 * filters/date-range/search (shared across levels) but drop sort + page — the two levels sort by
 * different columns, so a carried-over sort key wouldn't map to a column on the destination level.
 *
 * The Candidates level needs the candidate-tracking module, which is enabled separately from applies,
 * so its tab is disabled without it. Disabled while the status is still loading — failing closed beats
 * offering a link to a page that would only answer with the unavailable state.
 */
export default function ReportLevelTabs({ active }: { active: ReportLevel }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { clientId } = useParams<{ clientId?: string }>();
  const { data: candidateTracking } = useModuleStatus(ModuleName.CandidateTracking, { clientId });
  const candidatesDisabled = !candidateTracking?.enabled;

  // Strip a trailing /candidates so we can re-append the right segment per tab; works in both contexts.
  const basePath = useMemo(
    () => location.pathname.replace(/\/candidates\/?$/i, ""),
    [location.pathname],
  );

  const search = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.delete(URL_PARAMS.ORDER_BY);
    params.delete(URL_PARAMS.DESCENDING);
    params.delete(URL_PARAMS.PAGE);
    const next = params.toString();
    return next ? `?${next}` : "";
  }, [location.search]);

  const tabs = useMemo(
    () => [
      { id: "applies", label: t("common.applies"), href: `${basePath}${search}`, content: <></> },
      {
        id: "candidates",
        label: t("common.candidates", "Candidates"),
        href: `${basePath}/candidates${search}`,
        disable: candidatesDisabled,
        content: <></>,
      },
    ],
    [basePath, candidatesDisabled, search, t],
  );

  return <Tabs tabs={tabs} defaultTabId={active} />;
}
